import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { usePortfolio, useRedeemInvestment } from '../../hooks/useInvestments';
import type { InvestmentProductType, ApplicationStatus } from '../../types/investments';

const typeLabels: Record<InvestmentProductType, string> = {
  CDB: 'CDB',
  LCI: 'LCI',
  LCA: 'LCA',
  FUND: 'Fundo',
  DEBENTURE: 'Debênture',
  CRI: 'CRI',
  CRA: 'CRA',
};

const statusLabels: Record<ApplicationStatus, string> = {
  PENDING: 'Pendente',
  CONFIRMED: 'Confirmado',
  ACTIVE: 'Ativo',
  MATURED: 'Vencido',
  REDEEMED: 'Resgatado',
  CANCELLED: 'Cancelado',
};

export function MyPortfolio() {
  const navigate = useNavigate();
  const { data: portfolio, isLoading } = usePortfolio();
  const redeemInvestment = useRedeemInvestment();

  const handleRedeem = async (applicationId: string) => {
    if (confirm('Deseja resgatar este investimento?')) {
      await redeemInvestment.mutateAsync({ applicationId });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Meu Portfólio</h1>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 mb-4">Você ainda não possui investimentos</p>
            <Button onClick={() => navigate('/banking/investments')}>
              Explorar Investimentos
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Meu Portfólio</h1>
          <p className="text-gray-500">Acompanhe seus investimentos</p>
        </div>
        <Button onClick={() => navigate('/banking/investments')}>
          Investir Mais
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Valor Investido</p>
            <p className="text-2xl font-bold">{formatCurrency(portfolio.totalInvested)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Valor Atual</p>
            <p className="text-2xl font-bold">{formatCurrency(portfolio.currentValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Rendimento Total</p>
            <p className={`text-2xl font-bold ${portfolio.totalYield >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(portfolio.totalYield)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Rentabilidade</p>
            <p className={`text-2xl font-bold ${portfolio.yieldPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPercent(portfolio.yieldPercentage)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Allocation */}
      {portfolio.allocation && portfolio.allocation.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Alocação por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {portfolio.allocation.map((item) => (
                <div key={item.type}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{typeLabels[item.type]}</span>
                    <span>{formatCurrency(item.amount)} ({item.percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Maturities */}
      {portfolio.upcomingMaturities && portfolio.upcomingMaturities.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Próximos Vencimentos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {portfolio.upcomingMaturities.map((item) => (
                <div key={item.applicationId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{item.productName}</p>
                    <p className="text-sm text-gray-500">
                      Vence em {new Date(item.maturityDate).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <p className="font-medium">{formatCurrency(item.currentValue)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Applications List */}
      <Card>
        <CardHeader>
          <CardTitle>Minhas Aplicações</CardTitle>
        </CardHeader>
        <CardContent>
          {portfolio.applications && portfolio.applications.length > 0 ? (
            <div className="space-y-4">
              {portfolio.applications.map((app) => (
                <div key={app.id} className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{app.product?.name || 'Investimento'}</h3>
                        <Badge variant="default">
                          {app.product?.type ? typeLabels[app.product.type] : '-'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Aplicado em {new Date(app.appliedAt).toLocaleDateString('pt-BR')}
                        {app.maturityDate && (
                          <> • Vence em {new Date(app.maturityDate).toLocaleDateString('pt-BR')}</>
                        )}
                      </p>
                    </div>
                    <Badge variant={app.status === 'ACTIVE' ? 'success' : 'default'}>
                      {statusLabels[app.status]}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-500">Aplicado</p>
                      <p className="font-medium">{formatCurrency(app.amount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Valor Atual</p>
                      <p className="font-medium">{formatCurrency(app.currentValue)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Rendimento Bruto</p>
                      <p className="font-medium text-green-600">{formatCurrency(app.grossYield)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Rendimento Líquido</p>
                      <p className="font-medium text-green-600">{formatCurrency(app.netYield)}</p>
                    </div>
                  </div>

                  {app.status === 'ACTIVE' && (
                    <div className="flex justify-end mt-4">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleRedeem(app.id)}
                        disabled={redeemInvestment.isPending}
                      >
                        Resgatar
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Nenhuma aplicação encontrada
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default MyPortfolio;
